import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Bookmark, Check, CircleCheck, CircleX, History, Lightbulb, Medal, NotebookPen, RotateCcw, TrendingUp, X, Zap,
} from 'lucide-react';
import { examService } from '../services/api';
import { CourseContext } from '../contexts/CourseContext';
import { AppShell, Badge, Button, Card, Chip, EmptyState, InfinityMark, Loader, SectionTitle } from '../components/ui';
import { extraNote } from '../utils/questionNote';
import { Confetti, LevelUpModal, MedalModal } from '../components/RewardModal';
import ShareModal from '../components/ShareModal';
import { fmtXp, iconFor, tierOf } from '../utils/gamification';
import './Results.css';

/** Resultado de um simulado finalizado, carregado do servidor (pode ser reaberto pelo histórico). */
function Results() {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseSlug, examId } = useParams();
  // Só comemora logo após finalizar; reabrir pelo histórico mostra a faixa, sem confete nem modais
  const [fresh] = useState(() => Boolean(location.state?.fresh));
  const [celebrations, setCelebrations] = useState(null);
  // Compartilhar a partir de um modal de recompensa; a fila de comemorações espera ele fechar
  const [share, setShare] = useState(null);
  const { currentCourse, selectCourse } = useContext(CourseContext);
  const [result, setResult] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all');

  const buildPath = (path) => (courseSlug ? `/curso/${courseSlug}${path}` : path);

  useEffect(() => {
    if (courseSlug) selectCourse(courseSlug);
  }, [courseSlug, selectCourse]);

  useEffect(() => {
    examService.getResult(examId)
      .then(setResult)
      .catch(() => setLoadError(true));
  }, [examId]);

  // Fila de comemorações, uma por vez: nível > medalhas (o resultado fica por baixo)
  useEffect(() => {
    if (!fresh || !result?.rewards || celebrations) return;
    const r = result.rewards;
    const queue = [];
    if (r.levelAfter > r.levelBefore) queue.push({ type: 'level' });
    (r.unlocked || []).forEach(medal => queue.push({ type: 'medal', medal }));
    setCelebrations(queue);
    // Recarregar a página não repete a comemoração
    navigate(location.pathname, { replace: true, state: null });
  }, [fresh, result, celebrations, navigate, location.pathname]);

  if (loadError) {
    return (
      <AppShell section="Resultado" courseName={currentCourse?.name}>
        <EmptyState
          file="resultado.xml"
          title="Resultado indisponível"
          description="Este simulado não existe, não é seu ou ainda não foi finalizado."
          action={<Button icon={ArrowLeft} onClick={() => navigate(buildPath('/simulados'))}>Ver meus simulados</Button>}
        />
      </AppShell>
    );
  }

  if (!result) {
    return (
      <AppShell section="Resultado" courseName={currentCourse?.name}>
        <Loader label="Carregando resultado..." />
      </AppShell>
    );
  }

  const { questions, answers, score } = result;
  const flaggedQuestions = result.flagged || [];
  const PASSING_SCORE = Number(result.passingPercentage ?? 61);
  const errorsSaved = true;

  const flaggedSet = new Set(flaggedQuestions);

  const passed = result.passed ?? parseFloat(score) >= PASSING_SCORE;
  const isQuestionCorrect = (q) => {
    const userAnswer = answers[q.id] || [];
    const correctAnswers = q.options.filter(opt => opt.isCorrect).map(opt => opt.optionLetter).sort();
    return JSON.stringify([...userAnswer].sort()) === JSON.stringify(correctAnswers);
  };
  const correctCount = questions.filter(isQuestionCorrect).length;
  const wrongCount = questions.length - correctCount;
  const scoreNum = parseFloat(score);

  const filters = [
    { value: 'all', label: 'Todas', count: questions.length },
    { value: 'wrong', label: 'Incorretas', count: wrongCount },
    { value: 'correct', label: 'Corretas', count: correctCount },
    { value: 'flagged', label: 'Marcadas', count: flaggedSet.size },
  ];
  const visibleQuestions = questions
    .map((q, index) => ({ q, index, ok: isQuestionCorrect(q) }))
    .filter(({ q, ok }) =>
      reviewFilter === 'all' ||
      (reviewFilter === 'wrong' && !ok) ||
      (reviewFilter === 'correct' && ok) ||
      (reviewFilter === 'flagged' && flaggedSet.has(q.id)));

  // Anel de progresso da nota
  const R = 84;
  const C = 2 * Math.PI * R;

  const rewards = result.rewards;
  const current = share ? null : celebrations?.[0];
  const nextCelebration = () => setCelebrations(q => q.slice(1));
  const goAchievements = () => navigate(buildPath('/conquistas'));
  const openShare = (options) => {
    setShare(options);
    nextCelebration();
  };

  return (
    <AppShell section="Resultado" courseName={currentCourse?.name}>
      <section className={`res-hero ${passed ? 'res-hero--pass' : 'res-hero--fail'} ${rewards ? 'res-hero--rewards' : ''}`}>
        {fresh && passed && <Confetti count={36} seed={3} />}
        <InfinityMark className="res-hero__mark" strokeWidth={4} />
        <div className="res-hero__body">
          <div className="res-hero__ring">
            <svg viewBox="0 0 200 200" aria-hidden="true">
              <circle cx="100" cy="100" r={R} className="res-ring__track" />
              <circle
                cx="100"
                cy="100"
                r={R}
                className="res-ring__value"
                strokeDasharray={`${(scoreNum / 100) * C} ${C}`}
                transform="rotate(-90 100 100)"
              />
            </svg>
            <div className="res-hero__score">
              <span className="res-hero__pct">{Math.round(scoreNum)}%</span>
              <span className="res-hero__pctlabel">de acerto</span>
            </div>
          </div>
          <div className="res-hero__text">
            <Badge tone={passed ? 'lime' : 'danger'} icon={passed ? CircleCheck : CircleX}>
              {passed ? 'Aprovado' : 'Reprovado'}
            </Badge>
            <h1>{passed ? 'Mandou bem!' : 'Continue estudando!'}</h1>
            <p>
              {passed
                ? 'Você atingiu a pontuação necessária para aprovação.'
                : 'Você não atingiu a pontuação mínima desta vez. Revise os erros e tente de novo.'}
            </p>
            <div className="res-hero__stats">
              <div><strong>{correctCount}/{questions.length}</strong><span>corretas</span></div>
              <div><strong>{PASSING_SCORE}%</strong><span>para aprovação</span></div>
              <div><strong>{flaggedSet.size}</strong><span>marcadas</span></div>
            </div>
            <div className="res-hero__actions">
              <Button variant="accent" icon={RotateCcw} onClick={() => navigate(buildPath('/config'))}>Novo simulado</Button>
              <Button variant="outline" icon={NotebookPen} onClick={() => navigate(buildPath('/erros'))}>
                Caderno de erros{errorsSaved ? ` (+${wrongCount})` : ''}
              </Button>
              <Button variant="ghost" icon={History} onClick={() => navigate(buildPath('/simulados'))}>Meus simulados</Button>
            </div>
          </div>
        </div>
        {rewards && <RewardStrip rewards={rewards} />}
      </section>

      {current?.type === 'level' && (
        <LevelUpModal
          rewards={rewards}
          courseName={currentCourse?.name}
          onClose={nextCelebration}
          onShare={() => openShare({ kind: 'level', level: { level: rewards.levelAfter, xp: rewards.courseXp } })}
        />
      )}
      {current?.type === 'medal' && (
        <MedalModal
          medal={current.medal}
          onClose={nextCelebration}
          onAlbum={goAchievements}
          onShare={() => openShare({ kind: 'medal', medal: current.medal })}
        />
      )}
      {share && (
        <ShareModal
          courseSlug={courseSlug}
          courseName={currentCourse?.name}
          kind={share.kind}
          level={share.level}
          medal={share.medal}
          onClose={() => setShare(null)}
        />
      )}

      <SectionTitle aside={`${visibleQuestions.length} de ${questions.length}`}>Revisão das questões</SectionTitle>
      <div className="res-filters">
        {filters.map(f => (
          <Chip key={f.value} active={reviewFilter === f.value} count={f.count} onClick={() => setReviewFilter(f.value)}>
            {f.label}
          </Chip>
        ))}
      </div>

      <div className="res-list">
        {visibleQuestions.length === 0 && (
          <EmptyState file="revisao.xml" title="Nenhuma questão neste filtro" description="Escolha outro filtro para ver as questões." />
        )}
        {visibleQuestions.map(({ q, index, ok }) => {
          const userAnswer = answers[q.id] || [];
          return (
            <Card key={q.id} padding="lg" className={`res-item ${ok ? 'res-item--ok' : 'res-item--bad'}`}>
              <div className="res-item__head">
                <span className="res-item__num">{String(index + 1).padStart(2, '0')}</span>
                <Badge tone={ok ? 'success' : 'danger'} icon={ok ? CircleCheck : CircleX}>{ok ? 'Correta' : 'Incorreta'}</Badge>
                {flaggedSet.has(q.id) && <Badge tone="warning" icon={Bookmark}>Marcada</Badge>}
                {q.category?.name && <Badge tone="neutral">{q.category.name}</Badge>}
              </div>
              <p className="res-item__q">{q.questionText}</p>
              {extraNote(q) && <p className="res-item__note"><strong>Nota:</strong> {extraNote(q)}</p>}

              <div className="res-options">
                {q.options.map(option => {
                  const wasSelected = userAnswer.includes(option.optionLetter);
                  const isCorrectOption = option.isCorrect;
                  const state = isCorrectOption ? 'is-correct' : wasSelected ? 'is-wrong' : '';
                  return (
                    <div key={option.optionLetter} className={`res-option ${state}`}>
                      <span className="res-option__letter">{option.optionLetter}</span>
                      <span className="res-option__text">{option.optionText}</span>
                      {wasSelected && (
                        <span className="res-option__mine">
                          {isCorrectOption ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
                          Sua escolha
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {q.explanation && q.explanation.trim() && (
                <div className="res-explain">
                  <Lightbulb size={18} aria-hidden="true" />
                  <div>
                    <strong>Explicação</strong>
                    <p>{q.explanation}</p>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </AppShell>
  );
}

/**
 * Base do hero: XP ganho, progresso no nível e, quando houver, medalha nova e mudança de
 * maestria ou de posição no ranking.
 */
function RewardStrip({ rewards: r }) {
  const leveledUp = r.levelAfter > r.levelBefore;
  const medal = r.unlocked?.[0];
  const mastery = r.masteryChanges?.[0];
  const rankUp = r.rankAfter > 0 && r.rankBefore > 0 && r.rankAfter < r.rankBefore;
  const MedalIcon = medal ? iconFor(medal.icon) : null;
  const medalTier = medal ? tierOf(medal.tier) : null;
  const masteryTier = mastery ? tierOf(mastery.tierAfter) : null;
  const cells = 2 + (medal ? 1 : 0) + (mastery || rankUp ? 1 : 0);

  return (
    <div className="res-rewards" style={{ '--cells': cells }}>
      <div className="res-rewards__cell">
        <span className="res-rewards__square"><Zap size={22} aria-hidden="true" /></span>
        <span className="res-rewards__text">
          <strong className="res-rewards__xp">+{fmtXp(r.xp)} XP</strong>
          <span>{r.breakdown?.length ? r.breakdown.map(b => `${fmtXp(b.xp)} ${b.label}`).join(' · ') : 'nenhum acerto desta vez'}</span>
        </span>
      </div>

      <div className="res-rewards__cell">
        <span className="res-rewards__ring" style={{ '--pct': `${r.pctAfter}%` }}>
          <span>{r.levelAfter}</span>
        </span>
        <span className="res-rewards__text">
          <strong>{leveledUp ? `Nível ${r.levelAfter} alcançado` : `${fmtXp(r.xpToNext)} XP para o nível ${r.levelAfter + 1}`}</strong>
          <span>
            {leveledUp ? `${fmtXp(r.xpToNext)} XP para o nível ${r.levelAfter + 1}` : `${r.pctBefore}% → ${r.pctAfter}%`}
          </span>
        </span>
      </div>

      {medal && (
        <div className="res-rewards__cell">
          <span className="res-rewards__medal" style={{ background: medalTier.bg, color: medalTier.fg }}>
            <MedalIcon size={22} aria-hidden="true" />
          </span>
          <span className="res-rewards__text">
            <strong>{medal.name}</strong>
            <span>
              {r.unlocked.length > 1 ? `${r.unlocked.length} medalhas desbloqueadas` : 'medalha desbloqueada'}
            </span>
          </span>
        </div>
      )}

      {(mastery || rankUp) && (
        <div className="res-rewards__cell">
          <span
            className="res-rewards__medal"
            style={mastery ? { background: masteryTier.bg, color: masteryTier.fg } : undefined}
          >
            {mastery ? <Medal size={22} aria-hidden="true" /> : <TrendingUp size={22} aria-hidden="true" />}
          </span>
          <span className="res-rewards__text">
            <strong>
              {mastery ? `${mastery.topic} agora é ${masteryTier.label.toLowerCase()}` : `Ranking: subiu para ${r.rankAfter}º`}
            </strong>
            <span>{mastery && rankUp ? `Ranking: subiu para ${r.rankAfter}º` : mastery ? 'maestria no tópico' : 'nesta semana'}</span>
          </span>
        </div>
      )}
    </div>
  );
}

export default Results;
